import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, ShieldCheck, Salad, HeartPulse, Activity, Download, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const switchMode = (m) => { setMode(m); setError(""); setResetSent(false); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
        setResetSent(true);
      } else if (mode === "register") {
        const u = await register(name, email, password);
        toast.success("Registrazione completata!");
        navigate(u.onboarding_completed ? "/dashboard" : "/onboarding");
      } else {
        const u = await login(email, password);
        toast.success("Accesso effettuato");
        navigate(u.onboarding_completed ? "/dashboard" : "/onboarding");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const adminGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 text-white overflow-hidden"
        style={{ background: "linear-gradient(135deg,#2E5BFF 0%,#7A2FF2 55%,#EC0B8C 100%)" }}>
        <img src="/SfondoDietFlix.png" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-black/30" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-3">
            <div className="inline-flex bg-white rounded-2xl px-4 py-2.5 shadow-lg">
              <img src="/DietFlix-logo.webp" alt="DietFlix" className="h-8" />
            </div>
            <span className="text-sm font-medium text-white/90 italic" data-testid="brand-claim">la tua nutrizione “ondemand”</span>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-heading text-4xl xl:text-5xl font-semibold leading-tight">
            La tua dieta su misura,<br />senza privazioni.
          </h1>
          <p className="text-primary-foreground/80 text-lg max-w-md leading-relaxed">
            Piani settimanali personalizzati, sostituzioni dinamiche dei pasti, monitoraggio del peso e
            interpretazione clinica delle analisi del sangue.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            {[["Piani DietFlix", Salad], ["Analisi cliniche", HeartPulse], ["Progressi", Activity]].map(([t, Ic]) => (
              <span key={t} className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-2 text-sm border border-white/20">
                <Ic className="h-4 w-4" /> {t}
              </span>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-primary-foreground/60 text-sm space-y-1">
          <div>Alimentazione consapevole & personalizzata</div>
          <div data-testid="credit-footer-brand">
            Piattaforma ideata e realizzata da{" "}
            <a href="mailto:awake1899@gmail.com?subject=DietFlix" className="text-white font-medium hover:underline">CN</a>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md space-y-8 animate-fade-up">
          <div className="lg:hidden relative overflow-hidden rounded-3xl bg-brand text-white p-6 shadow-xl" data-testid="login-mobile-hero">
            <div className="hero-orb h-32 w-32 bg-amber-300 -right-8 -top-10" />
            <div className="relative space-y-3">
              <div className="inline-flex bg-white rounded-xl px-3 py-1.5 shadow"><img src="/DietFlix-logo.webp" alt="DietFlix" className="h-7" /></div>
              <div className="font-heading text-2xl font-semibold leading-tight">La tua dieta su misura,<br />senza privazioni.</div>
              <span className="text-sm font-medium text-white/85 italic block" data-testid="brand-claim-mobile">la tua nutrizione “ondemand”</span>
            </div>
          </div>

          <div>
            <h2 className="font-heading text-3xl font-semibold">{mode === "register" ? "Crea il tuo account" : mode === "forgot" ? "Password dimenticata" : "Bentornato"}</h2>
            <p className="text-muted-foreground mt-2">
              {mode === "register" ? "Registrati gratis: profilo e anamnesi inclusi." : mode === "forgot" ? "Inserisci la tua email: ti invieremo un link per scegliere una nuova password." : "Accedi al tuo profilo per continuare il percorso."}
            </p>
          </div>

          {mode === "forgot" && resetSent ? (
            <div className="rounded-2xl border border-primary/30 bg-primary/10 p-5 space-y-3" data-testid="forgot-success">
              <div className="flex items-center gap-2 font-medium"><MailCheck className="h-5 w-5 text-primary" /> Controlla la tua email</div>
              <p className="text-sm text-muted-foreground">Se <b>{email}</b> è registrata, riceverai a breve un link valido 1 ora per reimpostare la password. Controlla anche la cartella spam.</p>
              <Button variant="outline" className="w-full" onClick={() => switchMode("login")} data-testid="forgot-back-login">Torna all'accesso</Button>
            </div>
          ) : (
          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            {mode === "register" && (
              <div className="space-y-2">
                <Label htmlFor="name">Nome</Label>
                <Input id="name" required value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Il tuo nome" data-testid="register-name-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@email.it" data-testid="login-email-input" />
            </div>
            {mode !== "forgot" && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {mode === "login" && (
                  <button type="button" onClick={() => switchMode("forgot")} className="text-xs text-primary font-medium hover:underline" data-testid="forgot-password-link">
                    Password dimenticata?
                  </button>
                )}
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" data-testid="login-password-input" />
            </div>
            )}
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full h-12 text-base btn-brand border-0 rounded-full" disabled={busy} data-testid="login-submit-button">
              <LogIn className="h-4 w-4 mr-2" /> {busy ? "Attendi…" : mode === "register" ? "Registrati" : mode === "forgot" ? "Invia link di reimpostazione" : "Accedi"}
            </Button>
            {mode === "forgot" && (
              <button type="button" onClick={() => switchMode("login")} className="w-full text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-cancel-button">
                Annulla e torna all'accesso
              </button>
            )}
          </form>
          )}

          <p className="text-sm text-center text-muted-foreground" data-testid="toggle-auth-mode">
            {mode === "register" ? "Hai già un account?" : "Non hai un account?"}{" "}
            <button type="button" className="text-primary font-medium hover:underline"
              onClick={() => switchMode(mode === "register" ? "login" : "register")}
              data-testid="toggle-auth-button">
              {mode === "register" ? "Accedi" : "Registrati gratis"}
            </button>
          </p>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-3 text-muted-foreground">Amministrazione</span>
            </div>
          </div>

          <Button variant="outline" className="w-full h-11" onClick={adminGoogleLogin} data-testid="admin-google-login-button">
            <ShieldCheck className="h-4 w-4 mr-2" /> Accedi come Super Admin (Google)
          </Button>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent("dietflix:install-request"))}
            className="w-full flex items-center justify-center gap-2 text-sm text-primary font-medium hover:underline"
            data-testid="home-install-button">
            <Download className="h-4 w-4" /> Installa l'app sul telefono (Android / iOS)
          </button>
          <p className="text-xs text-muted-foreground text-center">
            Gli account utente vengono creati dall'amministratore.
          </p>
          <p className="text-xs text-muted-foreground text-center pt-2 border-t" data-testid="credit-footer">
            <span className="block pt-3">Piattaforma ideata e realizzata da{" "}
            <a href="mailto:awake1899@gmail.com?subject=DietFlix" className="text-primary font-medium hover:underline" data-testid="credit-email-link">CN</a></span>
          </p>
        </div>
      </div>
    </div>
  );
}
